import React, { useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Divider,
  IconButton,
  MenuItem,
  MenuList,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material'

import { logout } from '~api/services/core/users'
import { CaseIcon, SettingsIcon } from '~assets/icons'
import { images } from '~assets/images'
import { Avatar } from '~components/common'
import EnvironmentIndicator from '~components/common/EnvironmentIndicator'
import Image from '~components/common/Image'
import { Icon } from '~components/icons'
import { FEATURE_FLAGS } from '~featureFlags'
import { useFeatureGate } from '~hooks/access/useFeatureGate'
import { type RootState } from '~redux/rootReducer'
import { useGetIdentityQuery } from '~redux/slices/apiSlice'
import { logoutUser } from '~redux/slices/authSlice'
import { type AppDispatch, persistor } from '~redux/store'
import { type IdentityId } from '~types'

interface TopHeaderProps {}

interface MenuItemWithIconProps {
  icon: React.ReactNode
  label: string
  onClick: () => void
}

const MenuItemWithIcon: React.FC<MenuItemWithIconProps> = ({ icon, label, onClick }) => {
  return (
    <MenuItem
      onClick={onClick}
      sx={{
        py: 1.5,
        px: 3,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }} aria-hidden>
        {icon}
      </Box>
      <Typography variant='body1'>{label}</Typography>
    </MenuItem>
  )
}

export const HomeAndSettingsButton: React.FC = () => {
  const user = useSelector((state: RootState) => state?.auth.user)
  const userId = useSelector((state: RootState) => state?.auth.userId)
  const navigate = useNavigate()
  const dispatch: AppDispatch = useDispatch()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const menuListRef = useRef<HTMLUListElement>(null)
  const open = Boolean(anchorEl)
  const { enabled: licensesAndPermitsEnabled } = useFeatureGate(FEATURE_FLAGS.LICENSES_AND_PERMITS)

  const { data: identityData } = useGetIdentityQuery(
    {
      user_id: userId || '',
      identity_id: user?.identity_id,
    },
    { skip: !user?.identity_id || !userId },
  )

  if (!user) {
    return null
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  const handleNavigateToCases = (): void => {
    navigate(`/my-cases`)
    handleClose()
  }

  const handleNavigateToLicenses = (): void => {
    navigate(`/my-licenses-and-permits`)
    handleClose()
  }

  const handleNavigateToSettings = (): void => {
    navigate(`/account`)
    handleClose()
  }

  const handleLogout = async (): Promise<void> => {
    handleClose()
    try {
      if (userId) {
        await logout(userId)
      }
      dispatch(logoutUser())
    } catch (error) {
      console.error('Logout failed', error)
    } finally {
      await persistor.purge()
      dispatch({ type: 'RESET_STATE' })
      window.location.href = '/'
      window.location.reload()
    }
  }

  return (
    <>
      <Tooltip title='Account menu'>
        <IconButton
          aria-haspopup='menu'
          aria-expanded={open || undefined}
          aria-controls={open ? 'account-menu' : undefined}
          onClick={handleClick}
          sx={{ padding: 0 }}
        >
          <Avatar
            identityId={user.identity_id as IdentityId}
            size={'30px'}
            alt={user.name ?? undefined}
          />
        </IconButton>
      </Tooltip>
      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              pt: 1,
              minWidth: 280,
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
              borderRadius: 2,
            },
          },
          transition: {
            onEntering: () => {
              menuListRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
            },
          },
        }}
      >
        {/* User Info */}
        <Box sx={{ px: 2, py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Avatar
              identityId={user.identity_id as IdentityId}
              size={'40px'}
              alt={user.name ?? undefined}
              ariaHidden
            />
            <Box>
              <Typography variant='h6'>{user?.name || 'My Account'}</Typography>
              {identityData?.identity?.email && (
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  {identityData.identity.email}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Divider variant='middle' role='presentation' />

        <MenuList
          id='account-menu'
          ref={menuListRef}
          sx={{ pt: 0 }}
          onKeyDown={(e) => {
            if (e.key === 'Tab') {
              e.preventDefault()
              handleClose()
            }
          }}
        >
          <MenuItemWithIcon
            icon={<CaseIcon color='var(--lightcarbon)' width={24} />}
            label='My Cases'
            onClick={handleNavigateToCases}
          />

          {licensesAndPermitsEnabled && (
            <MenuItemWithIcon
              icon={<Icon name='license' />}
              label='My Licenses and Permits'
              onClick={handleNavigateToLicenses}
            />
          )}

          <MenuItemWithIcon
            icon={<SettingsIcon color='var(--lightcarbon)' width={24} />}
            label='Account Settings'
            onClick={handleNavigateToSettings}
          />

          <Divider variant='middle' role='presentation' />

          <MenuItem
            onClick={handleLogout}
            sx={{
              py: 1.5,
              px: 3,
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Box sx={{ mr: 2, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
              <Icon name='Logout' size={24} aria-hidden />
            </Box>
            <Typography variant='body1'>Sign Out</Typography>
          </MenuItem>
        </MenuList>
      </Popover>
    </>
  )
}

const TopHeader: React.FC<TopHeaderProps> = () => {
  return (
    <>
      <EnvironmentIndicator position='bottom-right' />
      <Box
        className='flex-v-align'
        sx={{
          gap: 2,
          backgroundColor: 'white',
          borderBottom: '1px solid #e0e0e0',
          p: 3,
          height: '8vh',
          position: 'sticky',
          top: 0,
        }}
      >
        <Box
          sx={{ cursor: 'pointer' }}
          onClick={() => (window.location.href = `https://polimorphic.com`)}
        >
          <Image src={images.textLogo} alt='Polimorphic Logo Link' size={'120px'} />
        </Box>
        <Box className='flex-grow' />
        <HomeAndSettingsButton />
      </Box>
    </>
  )
}

export default TopHeader
